// ============================================================================
// CONSTANTS - Centralized chart type definitions
// ============================================================================

// All supported chart types
export const ALL_CHART_TYPES = [
  "BAR_CLUSTERED",
  "BAR_STACKED_100",
  "COLUMN_CLUSTERED",
  "COLUMN_STACKED_100",
  "PIE",
  "PIE_EXPLODED",
  "DOUGHNUT",
];

// Chart types that are pie-like (no series concept, only slices)
export const PIE_TYPES = ["PIE", "PIE_EXPLODED", "DOUGHNUT"];

// Chart types that are clustered (inverted series/category mapping)
export const CLUSTERED_TYPES = ["BAR_CLUSTERED", "COLUMN_CLUSTERED"];

// Chart types that are stacked (inverted category/series logic)
export const STACKED_TYPES = ["BAR_STACKED", "BAR_STACKED_100", "COLUMN_STACKED", "COLUMN_STACKED_100"];

// Chart type labels for display
export const BASE_TYPE_LABELS = {
  BAR_CLUSTERED: "Bar Clustered",
  BAR_STACKED: "Bar Stacked",
  BAR_STACKED_100: "Bar Stacked 100%",
  COLUMN_CLUSTERED: "Column Clustered",
  COLUMN_STACKED: "Column Stacked",
  COLUMN_STACKED_100: "Column Stacked 100%",
  PIE: "Pie",
  PIE_EXPLODED: "Pie Exploded",
  DOUGHNUT: "Doughnut",
  LINE: "Line",
  LINE_MARKERS: "Line with Markers",
  AREA: "Area",
  AREA_STACKED: "Area Stacked",
  RADAR: "Radar",
  XY_SCATTER: "Scatter",
};

// ============================================================================
// HELPER FUNCTIONS - Exported for use in components
// ============================================================================

/**
 * Get display label for a chart base type
 */
export function getBaseTypeLabel(baseType) {
  return BASE_TYPE_LABELS[baseType] || baseType || "Unknown";
}

/**
 * Check if a chart type is pie-like
 */
export function isPieType(baseType) {
  return PIE_TYPES.includes(baseType);
}

/**
 * Check if a chart type is clustered
 */
export function isClusteredType(baseType) {
  return CLUSTERED_TYPES.includes(baseType);
}

export function isNpsTemplate(template) {
  return template?.chart_type === "nps";
}

export function isNpsCard(card) {
  return card?.question_type === "nps" || (card?.question_type === "matrix" && card?.matrix_type === "nps");
}

/**
 * Check if a base type is compatible with cross-tabulation (pie types don't support multiple series)
 */
export function isCrosstabCompatible(baseType) {
  return !PIE_TYPES.includes(baseType);
}

/**
 * Get compatible base types for a question type (mirrors backend logic)
 */
export function getCompatibleBaseTypes(questionType, matrixType = null) {
  if (questionType === "matrix" && matrixType === "multiple") return CLUSTERED_TYPES;

  const compatibility = {
    // Single response types - all charts compatible
    single: ALL_CHART_TYPES,
    nps: ALL_CHART_TYPES,
    rating: ALL_CHART_TYPES,
    slider: ALL_CHART_TYPES,
    image_choice: ALL_CHART_TYPES,
    // Multiple response types - only clustered
    multiple: CLUSTERED_TYPES,
    ranking: CLUSTERED_TYPES,
    image_choice_multiple: CLUSTERED_TYPES,
    // Matrix - only stacked 100%
    matrix: ["BAR_STACKED_100", "COLUMN_STACKED_100"],
    // Open questions (codified) - only clustered bars
    open: CLUSTERED_TYPES,
    open_multiple: CLUSTERED_TYPES,
  };
  return compatibility[questionType] || [];
}
